// src/components/devices/SmartLockManager.js
import React, { useState, useContext, useEffect } from 'react';
import { RotateCcw } from 'lucide-react';
import { Button } from '../ui/button';
import PageHeader from '../ui/page-header';
import { AppContext } from '../../context/AppContext';
import { useAuth } from '../../context/AuthContext';
import SmartLockCard from './SmartLockCard';
import SmartLockDetails from './SmartLockDetails';
import LockDiscovery from './LockDiscovery';


/**
 * Component to manage smart locks
 */
const SmartLockManager = () => {
  const {
    smartLocks,
    refreshSmartLocks,
    startSmartLockScan,
    lockSmartLock,
    unlockSmartLock,
    prepareSmartLock
  } = useContext(AppContext);

  const { isAdmin } = useAuth();

  const [selectedLock, setSelectedLock] = useState(null);
  const [isScanning, setIsScanning] = useState(false);

  // Refresh locks on mount
  useEffect(() => {
    refreshSmartLocks();
  }, [refreshSmartLocks]);

  // Keep the hub's BLE session warm while the user is actually looking at
  // this page: heartbeat every 5s (the hub holds the link 8s past the last
  // prepare, so taps hit the ~120ms fast path instead of a 1-2s cold
  // connect). Gated on tab visibility — a background tab must never pin the
  // link and drain the lock battery. The hub also caps continuous warm time
  // server-side. Best-effort.
  useEffect(() => {
    if (!smartLocks.length) return;
    const warm = () => {
      if (document.visibilityState !== 'visible') return;
      smartLocks.forEach((lock) => prepareSmartLock(lock.lockMac));
    };
    warm();
    const interval = setInterval(warm, 5000);
    document.addEventListener('visibilitychange', warm);
    return () => {
      clearInterval(interval);
      document.removeEventListener('visibilitychange', warm);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [smartLocks.length]);

  // Handle toggle of a lock
  const handleToggleLock = async (mac, isLocked) => {
    const action = isLocked ? 'unlock' : 'lock';

    try {
      if (action === 'lock') {
        await lockSmartLock(mac);
      } else {
        await unlockSmartLock(mac);
      }
    } catch (error) {
      console.error('Error toggling lock:', error);
    }
  };

  // Handle view details of a lock
  const handleViewDetails = (lock) => {
    setSelectedLock(lock);
  };

  // Handle close details
  const handleCloseDetails = () => {
    setSelectedLock(null);
    refreshSmartLocks();
  };

  // Handle scan for locks
  const handleScanForLocks = async () => {
    try {
      setIsScanning(true);
      await startSmartLockScan(15000);
    } catch (error) {
      console.error('Error scanning for locks:', error);
      setIsScanning(false);
    }
  };

  // Handle lock initialized
  const handleLockInitialized = () => {
    refreshSmartLocks();
    setIsScanning(false);
  };

  // Handle stop scan
  const handleStopScan = async () => {
    setIsScanning(false);
  };

  return (
    <div>
      <PageHeader
        title="Smart Locks"
        subtitle="Manage access to your home"
        onAdd={isAdmin() ? handleScanForLocks : undefined}
        addDisabled={isScanning}
        addLabel="Add Lock"
        showAdd={isAdmin()}
        menuItems={[
          { label: 'Refresh', icon: RotateCcw, onClick: refreshSmartLocks, disabled: isScanning }
        ]}
      />

      <div className="px-4 py-6 space-y-6">

      {isScanning ? (
        <LockDiscovery onLockInitialized={handleLockInitialized} />
      ) : !smartLocks || smartLocks.length === 0 ? (
        isAdmin() ? (
          <div className="text-center py-12">
            <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-primary/10 flex items-center justify-center">
              <RotateCcw className="h-10 w-10 text-primary" />
            </div>
            <h3 className="text-lg font-semibold mb-2">Add Lock</h3>
            <p className="text-sm text-muted-foreground max-w-[280px] mx-auto mb-6">
              Tap the + button to add your first smart lock
            </p>
          </div>
        ) : (
          <div className="text-center py-12">
            <p className="text-muted-foreground">No smart locks configured. Contact an admin to add locks.</p>
          </div>
        )
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {smartLocks.map((lock) => (
            <SmartLockCard
              key={lock.lockMac}
              lock={lock}
              onToggle={handleToggleLock}
              onDetails={handleViewDetails}
            />
          ))}
        </div>
      )}
      </div>

      {isScanning && (
        <div className="fixed bottom-20 md:bottom-6 left-0 right-0 px-4">
          <Button
            onClick={handleStopScan}
            className="w-full h-12 rounded-full shadow-lg"
          >
            Stop Scanning
          </Button>
        </div>
      )}

      {selectedLock && (
        <SmartLockDetails
          lock={selectedLock}
          onClose={handleCloseDetails}
        />
      )}
    </div>
  );
};

export default SmartLockManager;